import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CONFIG } from '@hero/core';
import { FormFieldTypes } from '@hero/web-types';
import type { UseVehicleFormHook } from '@hero/web-hooks';
import type { VehiclePlateType } from '@hero/schema';
import { fileToBase64 } from '@hero/web-utils';
import { Form, FormControl } from '../../../ui/Form';
import { SelectItem } from '../../../ui/Select';
import { CustomFormField } from '../../../app/CustomFormField';
import { Button } from '../../../ui/Button';
import { Spinner } from '../../../app/Spinner';
import { VehicleTypeSelector } from '../VehicleTypeSelector';
import { DriverSelector } from '../../Driver/Selector/DriverSelector';
import { FileUploader } from '../../../app/File/FileUploader/FileUploader';
import { LogistiFieldBadge } from '../../../app/LogistiFieldBadge';
import { InputLabel } from '../../../ui/Input';

const ACCEPTED_IMAGE_TYPES: Record<string, string[]> = {
  'image/jpeg': [],
  'image/png': [],
  'image/webp': [],
};

const PLATE_TYPES: VehiclePlateType[] = [
  'private_transport',
  'public_transport',
  'heavy_equipment',
  'export',
  'temporary',
];

const ACCEPTED_REGISTRATION_TYPES: Record<string, string[]> = {
  'image/jpeg': [],
  'image/png': [],
  'image/webp': [],
  'application/pdf': [],
};

export interface VehicleFormProps {
  formHook: UseVehicleFormHook;
  hideSubmitButton?: boolean;
  isLoading?: boolean;
}

export function VehicleForm({
  formHook,
  hideSubmitButton = false,
  isLoading = false,
}: VehicleFormProps) {
  const { t } = useTranslation(['m-vehicle', 'c-main']);
  const { form, onValidSubmit } = formHook;
  const [vehicleImageFiles, setVehicleImageFiles] = useState<File[]>([]);
  const [registrationFiles, setRegistrationFiles] = useState<File[]>([]);

  const existingVehicleImageUrls = (form.watch('vehicleImages') ?? [])
    .map((img: any) => img?.url ?? img?.upload ?? '')
    .filter((u: string) => u.startsWith('http'));
  const existingRegUrl = (() => {
    const v = form.watch('registrationImage');
    const url = v?.url ?? v?.upload ?? '';
    return url.startsWith('http') ? url : '';
  })();

  const yearOptions = () => {
    const now = new Date().getUTCFullYear();
    return Array.from({ length: 46 }, (_, i) => now - i);
  };

  const handleVehicleImagesChange = async (files: File[], field: any) => {
    setVehicleImageFiles(files);
    const mapped = await Promise.all(
      files.map(async f => {
        const base64 = await fileToBase64(f);
        return { upload: base64 };
      }),
    );
    field.onChange(mapped);
  };

  const handleRegistrationChange = async (files: File[], field: any) => {
    setRegistrationFiles(files);
    if (files[0]) {
      const base64 = await fileToBase64(files[0]);
      field.onChange({ upload: base64 });
    } else {
      field.onChange(undefined);
    }
  };

  return (
    <Form {...form}>
      <div className="space-y-4">
        {/* Type & Plate */}
        <div className="grid grid-cols-2 gap-3">
          <CustomFormField
            filedType={FormFieldTypes.SKELETON}
            control={form.control}
            name="vehicle"
            label={t('form.type.title')}
            required
            renderSkeleton={(field: any) => (
              <FormControl>
                <VehicleTypeSelector
                  value={field.value}
                  onVehicleChange={val => field.onChange(val)}
                />
              </FormControl>
            )}
          />
          <CustomFormField
            control={form.control}
            name="plate"
            filedType={FormFieldTypes.INPUT}
            required
            label={t('form.plate.title')}
            placeholder={t('form.plate.placeholder')}
          />
        </div>

        {/* Plate category — no default is safe. Bayan puts this on the
            government document, so Hero asks rather than guessing. */}
        <LogistiFieldRow label={t('form.plate_type.title')} variant="required">
          <CustomFormField
            control={form.control}
            name="plateType"
            filedType={FormFieldTypes.SELECT}
            placeholder={t('form.plate_type.placeholder')}>
            {PLATE_TYPES.map(plateType => (
              <SelectItem key={plateType} value={plateType}>
                {t(`form.plate_type.options.${plateType}`)}
              </SelectItem>
            ))}
          </CustomFormField>
        </LogistiFieldRow>

        {/* Driver — optional, but recommended for Logisti so the
            integration can link the vehicle to a verified driver. */}
        <LogistiFieldRow label={t('form.driver.title')}>
          <CustomFormField
            filedType={FormFieldTypes.SKELETON}
            control={form.control}
            name="driverId"
            renderSkeleton={(field: any) => (
              <FormControl>
                <DriverSelector
                  selectedDrivers={field.value}
                  onDriversChange={drivers => {
                    field.onChange(drivers?.[0]?._id ?? undefined);
                  }}
                  onDriverSave={driver => {
                    field.onChange(driver._id);
                  }}
                  allowCreateNew
                  placeholder={t('modal.select_driver_placeholder')}
                />
              </FormControl>
            )}
          />
        </LogistiFieldRow>
        {/* TODO: Vehicle Images — hidden until upload flow is finalized */}
        {/* Registration Image — recommended for Logisti */}
        <LogistiFieldRow label={t('form.registration_pdf.title')}>
          <CustomFormField
            filedType={FormFieldTypes.SKELETON}
            control={form.control}
            name="registrationImage"
            renderSkeleton={(field: any) => (
              <FormControl>
                <FileUploader
                  files={registrationFiles.length > 0 ? registrationFiles : undefined}
                  onChange={files => handleRegistrationChange(files, field)}
                  acceptedFileTypes={ACCEPTED_REGISTRATION_TYPES}
                  label={t('form.registration_pdf.title')}
                  variant="compact"
                  existingUrls={existingRegUrl ? [existingRegUrl] : undefined}
                  status={registrationFiles.length > 0 || existingRegUrl ? 'success' : 'neutral'}
                />
              </FormControl>
            )}
          />
        </LogistiFieldRow>
        {/* Year & Brand */}
        <div className="grid grid-cols-2 gap-3">
          <LogistiFieldRow label={t('form.year.title')}>
            <CustomFormField
              control={form.control}
              name="year"
              filedType={FormFieldTypes.SELECT}
              placeholder={t('form.year.title')}>
              {yearOptions().map(year => (
                <SelectItem key={year} value={String(year)}>
                  {year}
                </SelectItem>
              ))}
            </CustomFormField>
          </LogistiFieldRow>
          <LogistiFieldRow label={t('form.brand.title')}>
            <CustomFormField
              control={form.control}
              name="brand"
              filedType={FormFieldTypes.SELECT}
              placeholder={t('form.brand.title')}>
              {CONFIG.vehicles.brands.map((brand: string) => (
                <SelectItem key={brand} value={brand}>
                  {t(`brands.${brand}`, { defaultValue: brand })}
                </SelectItem>
              ))}
            </CustomFormField>
          </LogistiFieldRow>
        </div>

        {/* Color & Country */}
        <div className="grid grid-cols-2 gap-3">
          <LogistiFieldRow label={t('form.color.title')}>
            <CustomFormField
              control={form.control}
              name="color"
              filedType={FormFieldTypes.SELECT}
              placeholder={t('form.color.title')}>
              {(['blue', 'red', 'green', 'black', 'white'] as const).map(color => (
                <SelectItem key={color} value={color}>
                  {t(`colors.${color}`, { defaultValue: color })}
                </SelectItem>
              ))}
            </CustomFormField>
          </LogistiFieldRow>
          <LogistiFieldRow label={t('form.country.title')}>
            <CustomFormField
              control={form.control}
              name="country"
              filedType={FormFieldTypes.SELECT}
              placeholder={t('form.country.title')}>
              {CONFIG.vehicles.countries.map((country: string) => (
                <SelectItem key={country} value={country}>
                  {t(`countries.${country}`, { defaultValue: country })}
                </SelectItem>
              ))}
            </CustomFormField>
          </LogistiFieldRow>
        </div>

        {!hideSubmitButton && (
          <Button
            type="button"
            className="w-full"
            disabled={isLoading}
            onClick={() => form.handleSubmit(onValidSubmit)()}>
            {isLoading ? (
              <>
                <Spinner className="mr-2" />
                <span>{t('c-main:async_operations.creating')}</span>
              </>
            ) : (
              <span>{t('c-main:crud_buttons.create')}</span>
            )}
          </Button>
        )}
      </div>
    </Form>
  );
}

/**
 * Label row that pairs an `InputLabel` with the shared
 * `LogistiFieldBadge` and renders the input below.
 *
 * Used inside VehicleForm to annotate every non-essential field as
 * "Recommended for Logisti" — vehicle creation only requires `type`
 * (`vehicle`) + `plate`. Every other field (year, brand, color,
 * country, registration image, driver) is recommended so the
 * Logisti integration can verify and dispatch the vehicle.
 */
function LogistiFieldRow({
  label,
  children,
  variant = 'recommended',
}: {
  label: string;
  children: React.ReactNode;
  /**
   * `required` for the handful of fields a Bayan waybill genuinely cannot be
   * issued without — currently just the plate category. Everything else is
   * `recommended`: the vehicle record is usable without it.
   */
  variant?: 'required' | 'recommended';
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <InputLabel label={label} className="mb-0" />
        <LogistiFieldBadge variant={variant} />
      </div>
      {children}
    </div>
  );
}
